// Lib
import { omit, forEach, isFunction } from 'lodash';

// Utils
import logger from '../utils/logger';
import { getIsElectron } from '../hybridApp/hybridAppUtils';
import { getUtmData } from './utmClientUtils';
import { isInstalledPwa } from '../pwaApp/pwaUtils';
import { isInIFrame } from '../utils/services/iframe';
import { manuallyReportError } from './rollbarService';
import { tryUntil } from '../../common/utils/functionUtils';
import { getApplicationPlatform } from '../utils/platformUtils';

// Constants
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../common/analytics/statsConstants';
import { EVENT_TYPE_NAMES } from '../../common/analytics/amplitudeEventTypesUtil';
import { ROLLBAR_LEVELS } from './rollbarConstants';

const warnAmplitudeNotAvailable = (fn) => (args) => {
    if (process.env.NODE_ENV === 'development') {
        logger.info(
            "%cAmplitude is attempting to be invoked, but it's not available",
            'background: #fffaed; color: #664712; padding: 2px 10px; border-radius: 5px;',
            fn.name,
            args,
        );
        return;
    }

    logger.warn("Amplitude is attempting to be invoked, but it's not available", fn.name, args);
};

const ifAmplitudeIsAvailable =
    (fn) =>
    (...args) =>
        !!window.amplitude && isFunction(window.amplitude.getInstance)
            ? fn(...args)
            : warnAmplitudeNotAvailable(fn)(...args);

export const getAmplitudeDeviceId = ifAmplitudeIsAvailable(() => window.amplitude.getInstance()?.options?.deviceId);

export const enableAmplitudeTracking = ifAmplitudeIsAvailable((enable) => {
    if (enable) {
        window.amplitude.getInstance().setOptOut(false);

        // Call enable tracking until Amplitude is initialised properly. This is required because we were
        // seeing cases where calling enableTracking does not reliably initialise Amplitude.
        tryUntil(
            () => window.amplitude.getInstance().enableTracking(),
            () => window.amplitude.getInstance()._isInitialized,
        )
            .then(() => {
                logger.info('%cAmplitude analytics successfully enabled', 'color: green');
            })
            .catch((error) => {
                manuallyReportError({
                    errorMessage: 'Amplitude: Fail to enable Amplitude tracking',
                    level: ROLLBAR_LEVELS.WARNING,
                    error,
                });
            });
    } else {
        window.amplitude.getInstance().setOptOut(true);
    }
});

export const setAmplitudeUser = ifAmplitudeIsAvailable((user) => {
    window.amplitude.getInstance().setUserId(user._id);
});

export const clearAmplitudeUser = ifAmplitudeIsAvailable(() => {
    window.amplitude.getInstance().setUserId(null);
});

export const sendAmplitudeUserProperties = ifAmplitudeIsAvailable(({ userProperties }) => {
    if (!userProperties) return;

    const hasExampleBoardId = !!userProperties[AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS];
    if (hasExampleBoardId) {
        const identify = new window.amplitude.Identify().append(
            AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS,
            userProperties[AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS],
        );
        window.amplitude.identify(identify);
    }

    const otherUserProperties = omit(userProperties, [AMPLITUDE_USER_PROPS.EXAMPLE_BOARDS]);

    window.amplitude.getInstance().setUserProperties(otherUserProperties);
});

export const sendAmplitudeEvent = ifAmplitudeIsAvailable(({ eventType, eventProperties, userProperties }) => {
    sendAmplitudeUserProperties({ userProperties });

    eventType && window.amplitude.getInstance().logEvent(eventType, eventProperties);
});

const manuallySetUtmData = ifAmplitudeIsAvailable(() => {
    const utmData = getUtmData(document.location.href, document.referrer);
    if (!utmData) return;

    const identify = new window.amplitude.Identify();

    forEach(utmData, (value, key) => {
        identify.set(key, value);
        identify.setOnce(`initial_${key}`, value);
    });

    window.amplitude.identify(identify);
});

export const initialiseAmplitude = ifAmplitudeIsAvailable((config) => {
    const amplitudeConfig = {
        // Defer initialisation until we confirm that user has consented to performance cookies (see OneTrustInit.js)
        deferInitialization: true,

        forceHttps: true,

        // Allows an amplitude device ID to be specified via the "amp_device_id" parameter if set
        deviceIdFromUrlParam: true,

        // Google click identifier (if used)
        includeGclid: true,

        // UTM (Urchin traffic monitor) parameters
        includeUtm: true,

        // HTTP referrer header
        includeReferrer: true,

        // This is to ensure that requests will be made regardless of exiting page
        transport: 'beacon',

        trackingOptions: {
            ip_address: false,
        },

        // FIXME It looks like this is causing the loss first viewed marketing page
        // This is required to support UTM parameters set on the URL via public boards
        // saveParamsReferrerOncePerSession: false,
    };

    amplitudeConfig.platform = getApplicationPlatform();

    window.amplitude
        .getInstance()
        .init(config.analytics.amplitudeApiKey, null, amplitudeConfig, () =>
            logger.info('%cAmplitude analytics successfully loaded', 'color: green'),
        );
});

export const initialiseAmplitudeData = ifAmplitudeIsAvailable((config) => {
    if (isInIFrame()) {
        sendAmplitudeEvent({
            eventType: EVENT_TYPE_NAMES.LOADED_IN_IFRAME,
            eventProperties: {
                referrer: document.referrer,
            },
        });
    }

    const isElectron = getIsElectron();
    if (isElectron) {
        window.amplitude.getInstance().setVersionName(config.appVersion);
        sendAmplitudeUserProperties({
            userProperties: {
                [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.DESKTOP_APP]: true },
            },
        });
    } else if (isInstalledPwa()) {
        sendAmplitudeUserProperties({
            userProperties: {
                [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.PWA]: true },
            },
        });
    } else {
        // Try to classify users as
        manuallySetUtmData();
    }
});
